import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { CREATE_STANDARD_DRINK_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { StandardDrink } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<StandardDrink.CreateRequest>({
        resolver: yupResolver(CREATE_STANDARD_DRINK_SCHEMA),
        defaultValues: {
            name: '',
            description: '',
            isAlcoholic: true,
        },
    });

    const mutateCreateStandardDrink = useMutation({
        mutationKey: ['standardDrink'],
        mutationFn: (data: StandardDrink.CreateRequest) =>
            Api.StandardDrinks.StandardDrink.postStandardDrink(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: StandardDrink.CreateRequest) => {
        return mutateCreateStandardDrink
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating standard drink!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Standard Drink
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Preparation Instructions"
                        name="preparationInstructions"
                        fullWidth
                    />
                    <CheckboxElement label="Is Alcoholic" name="isAlcoholic" />
                    <CheckboxElement label="Is Shot" name="isShot" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
